import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSave, FiArrowLeft, FiUser, FiFileText, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { cargoService } from '../../services/api';
import './CadastroCargo.css'; // Reutilizar o CSS do cadastro

const EditarCargo = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    ativo: true
  });
  const [originalData, setOriginalData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      carregarCargo();
    }
  }, [id]);

  const carregarCargo = async () => {
    try {
      setLoadingData(true);
      const response = await cargoService.getById(id);
      
      if (response.success) {
        const cargoData = {
          nome: response.data.nome || '',
          descricao: response.data.descricao || '',
          ativo: response.data.ativo !== undefined ? response.data.ativo : true
        };
        setFormData(cargoData);
        setOriginalData(cargoData);
      } else {
        toast.error('Cargo não encontrado');
        navigate('/cargos');
      }
    } catch (error) {
      console.error('Erro ao carregar cargo:', error);
      toast.error('Erro ao carregar dados do cargo');
      navigate('/cargos');
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome do cargo é obrigatório';
    } else if (formData.nome.trim().length < 2) {
      newErrors.nome = 'Nome deve ter pelo menos 2 caracteres';
    } else if (formData.nome.trim().length > 100) {
      newErrors.nome = 'Nome deve ter no máximo 100 caracteres';
    }

    if (formData.descricao && formData.descricao.length > 500) {
      newErrors.descricao = 'Descrição deve ter no máximo 500 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasChanges()) {
      toast.info('Nenhuma alteração foi feita');
      return;
    }
    
    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    setLoading(true);
    try {
      const response = await cargoService.update(id, formData);
      
      if (response.success) {
        toast.success('Cargo atualizado com sucesso!');
        navigate('/cargos');
      } else {
        toast.error(response.message || 'Erro ao atualizar cargo');
      }
    } catch (error) {
      console.error('Erro ao atualizar cargo:', error);
      toast.error('Erro ao atualizar cargo');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges()) {
      if (window.confirm('Tem certeza que deseja cancelar? As alterações não salvas serão perdidas.')) {
        navigate('/cargos');
      }
    } else {
      navigate('/cargos');
    }
  };

  const handleReset = () => {
    if (window.confirm('Tem certeza que deseja desfazer todas as alterações?')) {
      setFormData({ ...originalData });
      setErrors({});
    }
  };

  if (loadingData) {
    return (
      <div className="cadastro-cargo-page">
        <div className="loading-container">
          <div className="loading-spinner">
            <FiRefreshCw className="spin" />
            <p>Carregando dados do cargo...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cadastro-cargo-page">
      <div className="page-header">
        <div className="header-content">
          <button 
            className="btn-back"
            onClick={() => navigate('/cargos')}
            type="button"
          >
            <FiArrowLeft />
          </button>
          <div>
            <h1>
              <FiUser className="page-icon" />
              Editar Cargo
            </h1>
            <p>Altere as informações do cargo</p>
          </div>
        </div>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="cargo-form">
          <div className="form-section">
            <div className="section-header">
              <h3>Informações Básicas</h3>
              {hasChanges() && (
                <button
                  type="button"
                  className="btn-reset"
                  onClick={handleReset}
                  title="Desfazer alterações"
                >
                  <FiRefreshCw /> Desfazer
                </button>
              )}
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nome" className="required">
                  <FiUser /> Nome do Cargo
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  placeholder="Ex: Ancião, Servo Ministerial, Pioneiro..."
                  className={errors.nome ? 'error' : ''}
                  maxLength={100}
                  required
                />
                {errors.nome && (
                  <span className="error-message">{errors.nome}</span>
                )}
                <small className="field-hint">
                  {formData.nome.length}/100 caracteres
                </small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="descricao">
                  <FiFileText /> Descrição
                </label>
                <textarea
                  id="descricao"
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleInputChange}
                  placeholder="Descreva as responsabilidades e características deste cargo..."
                  rows={4}
                  className={errors.descricao ? 'error' : ''}
                  maxLength={500}
                />
                {errors.descricao && (
                  <span className="error-message">{errors.descricao}</span>
                )}
                <small className="field-hint">
                  {formData.descricao.length}/500 caracteres (opcional)
                </small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="ativo"
                    name="ativo"
                    checked={formData.ativo}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="ativo" className="checkbox-label">
                    Cargo ativo
                    <small>Cargos ativos podem ser atribuídos aos colaboradores</small>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancelar
            </button>
            
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !hasChanges()}
            >
              {loading ? (
                <>
                  <div className="spinner" />
                  Salvando...
                </>
              ) : (
                <>
                  <FiSave /> Salvar Alterações
                </>
              )}
            </button>
          </div>
        </form>

        <div className="form-sidebar">
          <div className="info-card">
            <h4>📝 Alterações</h4>
            {hasChanges() ? (
              <div className="changes-info">
                <p className="changes-detected">Alterações detectadas:</p>
                <ul className="changes-list">
                  {formData.nome !== originalData.nome && (
                    <li><strong>Nome:</strong> alterado</li>
                  )}
                  {formData.descricao !== originalData.descricao && (
                    <li><strong>Descrição:</strong> alterada</li>
                  )}
                  {formData.ativo !== originalData.ativo && (
                    <li><strong>Status:</strong> {formData.ativo ? 'ativado' : 'desativado'}</li>
                  )}
                </ul>
                <p className="save-reminder">💡 Lembre-se de salvar suas alterações!</p>
              </div>
            ) : (
              <p className="no-changes">Nenhuma alteração foi feita ainda.</p>
            )}
          </div>

          <div className="info-card">
            <h4>💡 Dicas de Edição</h4>
            <ul>
              <li><strong>Nome:</strong> Seja descritivo e específico</li>
              <li><strong>Status:</strong> Desativar remove das opções de seleção</li>
              <li><strong>Histórico:</strong> Alterações são mantidas no histórico</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditarCargo; 